<template>
    <oss-dialog
        v-model="dialog"
        :title="tagDialogTitle"
        :validLoading="tagLoading"
        @valid="upsertTag()"
        @close="resetTag()"
        :validDisabled="validDisabled()"
        width="50vw"
    >
        <v-tabs
            v-model="tabStep"
            fixed-tabs
            style="background-color: transparent !important; border-radius: 8px; overflow: hidden"
        >
            <v-tab value="0" href="#0" v-if="showTabs"><strong>Description</strong></v-tab>
            <v-tab value="1" href="#1" v-if="showTabs"><strong>Produits</strong></v-tab>
        </v-tabs>
        <v-tabs-window v-model="tabStep">
            <v-tabs-window-item value="0">
                <v-row class="mx-4">
                    <v-col cols="12">
                        <v-text-field-filled
                            v-model="currentTag.name"
                            outlined
                            label="Nom"
                            hide-details
                            dense
                            v-if="!isTagDistribution"
                            class="my-3"
                        />
                        <strong v-else>{{ currentTagName }}</strong>
                        <v-select-filled
                            v-model="currentTag.primaryLog"
                            :items="primaryLogs"
                            :item-title="logTitle"
                            dense
                            outlined
                            class="mt-2"
                            v-if="currentTag.primaryLog"
                        />
                    </v-col>
                </v-row>
                <div v-if="isTagDistribution && currentTag.percentageTags">
                    <v-row class="mx-4">
                        <v-col cols="8">
                            <strong>Etiquette</strong>
                        </v-col>
                        <v-col cols="4">
                            <strong>Pourcentage</strong>
                        </v-col>
                    </v-row>
                    <v-row
                        v-for="(tag, index) in currentTag.percentageTags"
                        :key="index"
                        class="mx-4"
                    >
                        <v-col cols="8">
                            {{ tag.name }}
                        </v-col>
                        <v-col cols="4">
                            <oss-text-field-filled
                                v-model="currentTag.percentages[index]"
                                hide-details
                                :min="0"
                                :max="1"
                            />
                        </v-col>
                    </v-row>
                    <v-row class="mx-4">
                        <v-col cols="8" class="d-flex justify-end">
                            <strong>Total</strong>
                        </v-col>
                        <v-col cols="4">
                            <strong>{{ percentageTotal }}</strong>
                        </v-col>
                    </v-row>
                </div>
            </v-tabs-window-item>
            <v-tabs-window-item value="1">
                <v-row class="mx-4">
                    <v-col cols="12">
                        <v-select-filled
                            v-model="currentTag.invoiceProductType"
                            :items="productTypes"
                            hide-details
                            outlined
                            density="compact"
                            style="pointer-events: auto"
                            label="Type de produit"
                            item-title="description"
                            item-value="value"
                            class="my-3 v-select-font-size"
                            @update:model-value="computeResourceAllocations()"
                        />
                    </v-col>
                </v-row>
                <template v-if="showAllocations">
                    <v-row no-gutters class="mx-3 py-1 align-center justify-center">
                        <v-col cols="2"> Activer </v-col>
                        <v-col cols="4"> Affectation </v-col>
                        <v-col cols="5" class="pl-2"> Pourcentage </v-col>
                    </v-row>
                    <v-row no-gutters class="pb-1 align-center justify-center">
                        <v-col cols="11">
                            <v-divider />
                        </v-col>
                    </v-row>
                    <template v-if="resourceDistributions.length > 0">
                        <v-row
                            no-gutters
                            class="mx-3 pb-1 align-center justify-center"
                            v-for="(item, i) in resourceDistributions"
                            :key="i"
                        >
                            <v-col cols="2">
                                <v-switch
                                    hide-details
                                    density="compact"
                                    v-model="item.active"
                                    class="pl-2 mt-0"
                                    @change="item.percentage = 0"
                                />
                            </v-col>
                            <v-col cols="4">
                                <span>{{ item.name }}</span>
                            </v-col>
                            <v-col cols="5">
                                <oss-text-field-filled
                                    :hide-details="true"
                                    suffix="%"
                                    :disabled="!item.active"
                                    class="ml-2 righted-input"
                                    v-model="item.percentage"
                                    :rules="percentageRules(i)"
                                />
                            </v-col>
                        </v-row>
                        <v-row class="mx-1 justify-center">
                            <v-col cols="7" class="d-flex justify-end">
                                <strong>Total</strong>
                            </v-col>
                            <v-col cols="4" class="d-flex justify-end">
                                <strong
                                    :style="'color:' + (totalPercentage() > 100 ? 'red' : 'black')"
                                    >{{ totalPercentage() + " %" }}</strong
                                >
                            </v-col>
                        </v-row>
                    </template>
                    <v-row class="ma-3 justify-center" v-else>
                        Il n'y a pas de ressources associées à ce type d'étiquette
                    </v-row>
                </template>
            </v-tabs-window-item>
        </v-tabs-window>
    </oss-dialog>
</template>

<script lang="ts">
import gql from "graphql-tag";
import { Vue, Component, Model, Prop, toNative } from "vue-facing-decorator";
import _ from "lodash";

import UPDATE_TAG from "../../graphql/tag/UpdateTag.gql";
import COMPANY_TAG_QUERY from "../../graphql/tag/PartitionTagsCompany.gql";
import UPSERT_TAG_DISTRIBUTION from "../../graphql/tag/UpsertTagDistribution.gql";

import { documentLogs } from "../../services/accountingItemServices";

@Component({
    emits: ["upsertedTag", "cancel"],
})
export class TagEditionDialog extends Vue {
    @Model() value: boolean;
    @Prop({ required: true, type: Object }) currentTag!: any;
    @Prop({ default: "" }) companyId: string; // companyId
    @Prop({ default: false }) distribution: boolean; // companyId

    tabStep = "0";

    primaryLogs = documentLogs.map((l) => ({ value: l }));

    productTypes = [
        { value: "", description: "" },
        { value: "PRODUCT", description: "Produit" },
        { value: "EXPENSE", description: "Achat" },
        { value: "EXPENSE_DIRECT", description: "Charges Directes" },
        { value: "EXPENSE_INDIRECT_SECONDARY", description: "Charges Indirectes secondaires" },
        {
            value: "EXPENSE_INDIRECT_PRIMARY_ON_PRODUCTS",
            description: "Charges Indirectes primaires sur les produits",
        },
        {
            value: "EXPENSE_INDIRECT_PRIMARY_ON_EXPENSES",
            description: "Charges Indirectes primaires sur les dépenses",
        },
    ];

    resourceDistributions = [];

    tags = [];

    get dialog() {
        return this.value;
    }

    set dialog(event) {
        this.value = event;
    }

    async beforeMount() {
        if (this.enabledStocks && !this.isTagDistribution) {
            const result = await this.$apollo.query({
                query: COMPANY_TAG_QUERY,
                variables: {
                    companyId: this.companyId,
                },
            });
            if (result.data?.tags?.length) {
                this.tags = result.data.tags.filter((tag) => tag.invoiceProductType);
            }
            this.computeResourceAllocations();
        }
    }

    get enabledStocks() {
        return this.$store?.state?.selected_company?.profile?.stock;
    }

    get isTagDistribution() {
        return this.currentTag.type === "TagDistribution";
    }

    get showTabs() {
        if (this.enabledStocks && !this.distribution && !this.isTagDistribution) {
            return true;
        }
        return false;
    }

    get showAllocations() {
        if (!this.tags.length) {
            return false;
        }
        const filteredValues = this.productTypes
            .filter((item) => item.value.includes("PRIMARY") || item.value.includes("SECONDARY"))
            .map((item) => item.value);
        if (filteredValues.includes(this.currentTag?.invoiceProductType)) {
            return true;
        }
        return false;
    }

    showTagDialog: boolean = false;
    tagLoading: boolean = false;

    async upsertTag() {
        let refresh = false;
        if (this.isTagDistribution) {
            if (!this.currentTag || !this.currentTag.partitionTag) {
                debugger;
                return true;
            }
            this.tagLoading = true;
            let percentageTags;
            if (this.currentTag.percentageTags && this.currentTag.percentageTags.length) {
                percentageTags = this.currentTag.percentageTags
                    .filter((t, index) => this.currentTag.percentages[index] !== 0)
                    .map((t) => ({ id: t.id }));
            }
            let percentages;
            if (this.currentTag.percentages && this.currentTag.percentages.length) {
                percentages = this.currentTag.percentages
                    .filter((p) => p !== 0)
                    .map((p) => p * 0.01);
            }
            await this.$apollo.mutate({
                mutation: UPSERT_TAG_DISTRIBUTION,
                variables: {
                    id: this.currentTag.id,
                    type: this.currentTag.type,
                    name: this.currentTagName,
                    companyId: this.companyId,
                    partitionTagId: this.currentTag.partitionTag
                        ? this.currentTag.partitionTag.id
                        : undefined,
                    percentages,
                    percentageTags,
                    primaryLog: this.currentTag.primaryLog || undefined,
                },
            });
            refresh = true;
        } else if (this.currentTag.id) {
            let data = null;
            let productType = undefined;
            if (this.enabledStocks) {
                productType = this.currentTag.invoiceProductType;
                data = this.currentTag.data;
                if (!data) {
                    data = {};
                }
                if (productType) {
                    if (
                        this.resourceDistributions.some((rd) => rd.active) &&
                        productType.includes("EXPENSE_INDIRECT")
                    ) {
                        data["stocks"] = this.resourceDistributions
                            .filter((rd) => rd.active)
                            .reduce((acc, { id, percentage }) => {
                                acc[id] = percentage;
                                return acc;
                            }, {});
                    }
                } else if (data["stocks"]) {
                    delete data.stocks;
                }
                if (!Object.keys(data).length) {
                    data = null;
                }
            }
            await this.$apollo.mutate({
                mutation: UPDATE_TAG,
                variables: {
                    id: this.currentTag.id,
                    name: this.currentTagName,
                    productType: productType || null,
                    data: data,
                },
            });
            refresh = true;
        } else {
            console.error("TagManager - upsertTag currentTag.id is null");
            return;
        }
        if (refresh) {
            this.$emit("upsertedTag", this.currentTag);
        }
        this.tagLoading = false;
        this.dialog = false;
    }

    validDisabled() {
        if (!this.currentTag) return true;
        if (this.isTagDistribution) {
            if (!this.currentTag.percentages) {
                return true;
            }
            let count = 0;
            for (const percentage of this.currentTag.percentages) {
                if (!percentage) {
                    continue;
                }
                count++;
            }
            if (count < 2) {
                return true;
            }
            if (Math.abs(this.percentageTotal - 100) > 1e-9) {
                return true;
            }
        } else if (this.enabledStocks && this.showAllocations) {
            if (this.totalPercentage() < 100 || this.totalPercentage() > 100) {
                return true;
            }
        }

        return false;
    }

    computeResourceAllocations() {
        const filter = this.currentTag?.invoiceProductType
            ? this.currentTag.invoiceProductType
            : "";
        const stockData = this.currentTag?.data?.stocks ? this.currentTag.data.stocks : [];
        let resourceDistributions = [];
        if (this.tags.length && filter) {
            let filteredTags = [];
            if (filter.includes("PRODUCTS") && this.tags.length) {
                filteredTags = this.tags.filter((tag) => tag.invoiceProductType === "PRODUCT");
            } else if (filter.includes("EXPENSES") && this.tags.length) {
                filteredTags = this.tags.filter(
                    (tag) => tag.invoiceProductType === "EXPENSE_DIRECT",
                );
            } else if (filter.includes("SECONDARY")) {
                filteredTags = this.tags.filter(
                    (tag) =>
                        tag.id !== this.currentTag.id &&
                        tag.invoiceProductType?.includes("PRIMARY"),
                );
            }
            if (filteredTags.length) {
                resourceDistributions = filteredTags.map((rd) => {
                    return {
                        id: rd.id,
                        name: rd.name,
                        active: false,
                        percentage: 0,
                    };
                });
            }
        }
        if (stockData) {
            for (const id of Object.keys(stockData)) {
                const index = resourceDistributions.findIndex((rd) => rd.id === id);
                if (index > -1) {
                    resourceDistributions[index].active = true;
                    resourceDistributions[index].percentage = stockData[id];
                }
            }
        }
        this.resourceDistributions = resourceDistributions;
    }

    percentageRules(i) {
        const total = this.totalPercentage(i);
        return [(v) => v >= 0, (v) => v <= 100 - total];
    }

    totalPercentage(excludeIndex = null) {
        return this.resourceDistributions.reduce((sum, rd, index) => {
            return index === excludeIndex ? sum : sum + rd.percentage;
        }, 0);
    }

    resetTag() {
        this.tabStep = "0";
        this.resourceDistributions = [];
        this.$emit("cancel");
    }

    get percentageTotal() {
        let total = 0;
        for (const percentage of this.currentTag.percentages) {
            if (!percentage) {
                continue;
            }
            total += percentage;
        }
        return total;
    }

    get tagDialogTitle() {
        let title = "";
        if (this.currentTag) {
            title += this.currentTag.id ? "Modifier " : "Créer ";
            title += this.isTagDistribution ? "une distribution d'étiquettes" : "une étiquette";
        }
        return title;
    }

    get currentTagName() {
        let name = this.currentTag.name;
        if (this.isTagDistribution && this.currentTag.percentageTags) {
            name = "";
            let index = 0;
            for (const percentage of this.currentTag.percentages) {
                if (!percentage) {
                    index++;
                    continue;
                }
                const tag = this.currentTag.percentageTags[index];
                if (tag) {
                    if (name) name += " - ";
                    name += tag.name + " " + percentage + "%";
                }
                index++;
            }
        }
        return name;
    }

    refresh() {}

    logTitle(primaryLog) {
        return "Journal " + this.$t("document.type." + primaryLog.value);
    }
}

export default toNative(TagEditionDialog);
</script>
